import React, { useState, useCallback, useEffect } from 'react';
import { useLocation as useRouterLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { RouteErrorBoundary } from '../ErrorBoundary';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import ChangePasswordModal from '../auth/ChangePasswordModal';
import BulkApprovalBanner from './BulkApprovalBanner';

const Layout = ({ children }) => {
  const location = useRouterLocation();
  const { settings, updateSettings } = useTheme();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [forcedChangeHandled, setForcedChangeHandled] = useState(false);

  // Auto-otworz modal gdy must_change_password=true po zalogowaniu
  useEffect(() => {
    if (user?.must_change_password && !forcedChangeHandled) {
      setShowChangePasswordModal(true);
      setForcedChangeHandled(true);
    }
  }, [user?.must_change_password, forcedChangeHandled]);

  // Nasluch eventu 'password-expired' z api.js interceptor - emitowany gdy backend
  // zwroci 403 PASSWORD_EXPIRED na dowolne API. Otworz modal zmiany hasla zamiast toast.
  useEffect(() => {
    const handler = () => setShowChangePasswordModal(true);
    window.addEventListener('password-expired', handler);
    return () => window.removeEventListener('password-expired', handler);
  }, []);

  // Ukryj sidebar/topbar na stronie Kolektora (mobilna aplikacja)
  const hideChrome = location.pathname === '/kolektor';

  const isDark = settings.theme_mode === 'dark';

  const toggleTheme = () => {
    updateSettings({ theme_mode: isDark ? 'light' : 'dark' });
  };

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

  // Zamknij menu przy zmianie strony
  useEffect(() => { closeMobileMenu(); }, [location.pathname, closeMobileMenu]);

  if (hideChrome) {
    return (
      <main style={{ paddingTop: 0 }}>
        <RouteErrorBoundary key={location.pathname}>
          {children}
        </RouteErrorBoundary>
      </main>
    );
  }

  return (
    <div className="v3-app">
      {/* Globalny pasek postepu masowego zatwierdzania — widoczny na kazdej
          podstronie, przetrwa nawigacje (stan zyje w BulkApprovalContext). */}
      <BulkApprovalBanner />
      <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(v => !v)}>
        {mobileMenuOpen ? '✕' : '☰'}
      </button>
      <div className={`mobile-sidebar-overlay${mobileMenuOpen ? ' active' : ''}`} onClick={closeMobileMenu} />
      <Sidebar onToggleTheme={toggleTheme} isDark={isDark} mobileOpen={mobileMenuOpen} />
      <div className="v3-main">
        <Topbar onOpenChangePassword={() => setShowChangePasswordModal(true)} />
        <div className="v3-content">
          <RouteErrorBoundary key={location.pathname}>
            {children}
          </RouteErrorBoundary>
        </div>
      </div>

      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        mustChange={!!user?.must_change_password}
        daysLeft={user?.password_days_left}
      />
    </div>
  );
};

export default Layout;
